import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { Button, ButtonGroup, Content, Footer, Heading, Modal } from "@hopper-ui/components";
import { useCallback, useEffect } from "react";
import { useBlocker } from "react-router";

interface Props {
    isDisabled: boolean;
    shouldForceOpen?: boolean;
    onDiscard: () => void;
    onClose?: () => void;
}

export function PreventNavigationModal({ isDisabled, shouldForceOpen, onDiscard, onClose } : Props) {
    const { tShared } = useSharedTranslation();

    const blockerFunction = useCallback(() => {
        if (isDisabled) {
            return false;
        }

        return true;
    }, [isDisabled]);

    const blocker = useBlocker(blockerFunction);

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
        };

        if (!isDisabled) {
            window.addEventListener("beforeunload", handleBeforeUnload);

            return () => {
                window.removeEventListener("beforeunload", handleBeforeUnload);
            };
        }
    }, [blocker.state, isDisabled]);

    const handleDiscard = useCallback(() => {
        onDiscard?.();
        if (blocker.state === "blocked") {
            blocker.proceed();
        }
    }, [onDiscard, blocker]);

    const handleOpenChange = useCallback((isOpen: boolean) => {
        if (!isOpen) {
            blocker.reset?.();
            onClose?.();
        }
    }, [blocker, onClose]);

    return (
        <Modal
            size="sm"
            isOpen={shouldForceOpen ? shouldForceOpen : blocker.state === "blocked"}
            onOpenChange={handleOpenChange}
        >
            {({ close }) => (
                <>
                    <Heading>
                        {tShared("preventNavigationModal.heading")}
                    </Heading>
                    <Content>
                        {tShared("preventNavigationModal.content")}
                    </Content>
                    <Footer>
                        <ButtonGroup UNSAFE_width="100%">
                            <Button
                                variant="secondary"
                                onPress={close}
                                flexGrow={1}
                            >
                                {tShared("preventNavigationModal.keepEditing")}
                            </Button>
                            <Button
                                variant="danger"
                                onPress={handleDiscard}
                                flexGrow={1}
                            >
                                {tShared("preventNavigationModal.discardChanges")}
                            </Button>
                        </ButtonGroup>
                    </Footer>
                </>
            )}
        </Modal>
    );
}
